'use client';

import { c as _c } from "react/compiler-runtime";
import { type Factiii } from '@shared/all/domains/factiiis';
import { fitFactiiiStrip } from '@shared/all/helpers/factiiiStrip';
import Link from 'next/link';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import Avatar from '../common/Avatar';
import Modal, { ModalContent, ModalHeader } from '../HOC/Modal';

// Gap between avatars, across and down.
const STRIP_GAP = 8;
// Width of the "+N" badge. Callers that share a row with the strip keep this
// much for it so the badge always has somewhere to render.
export const FACTIII_STRIP_OVERFLOW_WIDTH = 32;
const DEFAULT_AVATAR_SIZE = 22;
// Avatar size in the modal that lists every factiii.
const MODAL_AVATAR_SIZE = 32;
// Avatars grow on hover; let that paint past the strip's clip edge.
const HOVER_CLIP_MARGIN = 4;
type Props = {
  factiiis: Factiii[];
  /** Height in px. The strip takes the full width it is given and shows as
   *  many rows of avatars as this height holds (always at least one). */
  height: number;
  avatarSize?: number;
  /** Replaces the default avatar link in the strip, e.g. to add a hover card. */
  renderItem?: (factiii: Factiii) => ReactNode;
  /** Heading of the modal the "+N" badge opens. */
  title?: string;
};

// Factiiis as avatars, placeable anywhere: give it a height and it fills the
// width, and what does not fit is counted by a "+N" badge that opens a modal
// listing every factiii (not only the hidden ones).
const FactiiiStrip = t0 => {
  const $ = _c(35);
  const {
    factiiis,
    height,
    avatarSize: t1,
    renderItem,
    title: t2
  } = t0;
  const avatarSize = t1 === undefined ? DEFAULT_AVATAR_SIZE : t1;
  const title = t2 === undefined ? "Factiiis" : t2;
  const ref = useRef(null);
  const [width, setWidth] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const el = ref.current;
      if (!el) {
        return;
      }
      const update = () => setWidth(el.getBoundingClientRect().width);
      update();
      const observer = new ResizeObserver(update);
      observer.observe(el);
      return () => observer.disconnect();
    };
    t4 = [];
    $[0] = t3;
    $[1] = t4;
  } else {
    t3 = $[0];
    t4 = $[1];
  }
  useEffect(t3, t4);
  let hiddenCount;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== avatarSize || $[3] !== factiiis || $[4] !== height || $[5] !== renderItem || $[6] !== width) {
    const visibleCount = width === null ? 0 : fitFactiiiStrip({
      width,
      height,
      count: factiiis.length,
      itemSize: avatarSize,
      gap: STRIP_GAP,
      overflowWidth: FACTIII_STRIP_OVERFLOW_WIDTH
    });
    hiddenCount = factiiis.length - visibleCount;
    let t10;
    if ($[13] !== avatarSize) {
      t10 = factiii => <Link key={factiii.id} href={`/f/${factiii.slug}`} onClick={_temp} aria-label={`Open f/${factiii.slug}`} className="flex shrink-0 transition-transform hover:z-10 hover:scale-110"><Avatar factiiiAvatarKey={factiii.avatarKey} factiiiId={factiii.id} size={avatarSize} isRetired={factiii.status === "RETIRED"} /></Link>;
      $[13] = avatarSize;
      $[14] = t10;
    } else {
      t10 = $[14];
    }
    const defaultItem = t10;
    t5 = ref;
    t6 = "factiii-strip";
    t7 = "flex w-full min-w-0 flex-1 flex-wrap content-center items-center overflow-clip";
    if ($[15] !== height) {
      t8 = {
        height,
        gap: STRIP_GAP,
        overflowClipMargin: HOVER_CLIP_MARGIN
      };
      $[15] = height;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    t9 = factiiis.slice(0, visibleCount).map(renderItem ?? defaultItem);
    $[2] = avatarSize;
    $[3] = factiiis;
    $[4] = height;
    $[5] = renderItem;
    $[6] = width;
    $[7] = hiddenCount;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
  } else {
    hiddenCount = $[7];
    t5 = $[8];
    t6 = $[9];
    t7 = $[10];
    t8 = $[11];
    t9 = $[12];
  }
  let t10;
  if ($[17] !== factiiis.length || $[18] !== hiddenCount || $[19] !== width) {
    t10 = width !== null && hiddenCount > 0 && <button type="button" aria-label={`Show all ${factiiis.length} factiiis`} onClick={e_0 => {
      e_0.stopPropagation();
      setModalOpen(true);
    }} style={{
      width: FACTIII_STRIP_OVERFLOW_WIDTH
    }} className="flex shrink-0 items-center justify-center rounded-md py-1 text-xs font-emphasis text-textSecondary transition-colors hover:bg-primary/10 hover:text-textPrimary">+{hiddenCount}</button>;
    $[17] = factiiis.length;
    $[18] = hiddenCount;
    $[19] = width;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t5 || $[23] !== t6 || $[24] !== t7 || $[25] !== t8 || $[26] !== t9) {
    t11 = <div ref={t5} data-id={t6} className={t7} style={t8}>{t9}{t10}</div>;
    $[21] = t10;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== factiiis || $[29] !== modalOpen || $[30] !== title) {
    t12 = modalOpen && <Modal closeHandler={() => setModalOpen(false)}><ModalHeader closeHandler={() => setModalOpen(false)}><h2 className="text-xl font-semibold text-textPrimary">{title} ({factiiis.length})</h2></ModalHeader><ModalContent><ul className="flex flex-col gap-1">{factiiis.map(factiii_0 => <li key={factiii_0.id}><Link href={`/f/${factiii_0.slug}`} onClick={() => setModalOpen(false)} className="flex items-center gap-3 rounded-md p-2 no-underline hover:bg-background-light"><Avatar factiiiAvatarKey={factiii_0.avatarKey} factiiiId={factiii_0.id} size={MODAL_AVATAR_SIZE} isRetired={factiii_0.status === "RETIRED"} /><div className="min-w-0"><p className="truncate font-emphasis text-textPrimary">{factiii_0.name}</p><p className="truncate text-sm text-textSecondary/70">f/{factiii_0.slug}</p></div></Link></li>)}</ul></ModalContent></Modal>;
    $[28] = factiiis;
    $[29] = modalOpen;
    $[30] = title;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t11 || $[33] !== t12) {
    t13 = <>{t11}{t12}</>;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  return t13;
};
export default FactiiiStrip;
function _temp(e) {
  return e.stopPropagation();
}